import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import {
	Paper,
	Grid,
	Typography,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Button,
	Divider,
	CircularProgress,
} from "@material-ui/core";
import AddOutlinedIcon from "@material-ui/icons/AddOutlined";
import AutorenewOutlinedIcon from "@material-ui/icons/AutorenewOutlined";
import PhoneIphoneOutlinedIcon from "@material-ui/icons/PhoneIphoneOutlined";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";

import PhoneFrame from "./PhoneFrame";
import TestConnectionDialog from "./TestConnectionDialog";

const useStyles = makeStyles((theme) => ({
	content: {
		flex: 1,
		overflowY: "auto",
		padding: theme.spacing(0.5, 0.75, 3),
		...theme.scrollbarStyles,
	},
	configPaper: {
		padding: theme.spacing(2.5),
		borderRadius: 16,
		border: `1px solid ${theme.custom.border}`,
	},
	configSubtitle: {
		marginBottom: theme.spacing(2),
	},
	field: {
		marginBottom: theme.spacing(1.5),
	},
	sectionTitle: {
		marginTop: theme.spacing(0.5),
		marginBottom: theme.spacing(1.5),
	},
	divider: {
		margin: theme.spacing(2, 0),
	},
	createSmallButton: {
		marginBottom: theme.spacing(0.5),
	},
	emptyConnection: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		textAlign: "center",
		gap: theme.spacing(1),
		padding: theme.spacing(3, 2),
		borderRadius: 12,
		border: `1px dashed ${theme.custom.border}`,
		backgroundColor: theme.custom.surfaceHover,
	},
	emptyConnectionIcon: {
		fontSize: 40,
		color: theme.custom.textSecondary,
		opacity: 0.6,
	},
	emptyConnectionTitle: {
		fontWeight: 700,
	},
	emptyConnectionDescription: {
		color: theme.custom.textSecondary,
		fontSize: ".85rem",
		maxWidth: 280,
	},
	loadingConnections: {
		display: "flex",
		justifyContent: "center",
		padding: theme.spacing(2),
	},
	stepsCard: {
		padding: theme.spacing(1.75),
		borderRadius: 12,
		backgroundColor: theme.custom.surfaceHover,
		border: `1px solid ${theme.custom.border}`,
	},
	stepsTitle: {
		marginBottom: theme.spacing(1.25),
	},
	stepRow: {
		display: "flex",
		alignItems: "flex-start",
		gap: theme.spacing(1.25),
		marginBottom: theme.spacing(1),
		"&:last-child": {
			marginBottom: 0,
		},
	},
	stepNumber: {
		width: 22,
		height: 22,
		borderRadius: "50%",
		backgroundColor: theme.palette.primary.main,
		color: "#fff",
		fontSize: ".7rem",
		fontWeight: 700,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		flexShrink: 0,
		marginTop: 1,
	},
	stepText: {
		fontSize: ".8rem",
		color: theme.palette.text.secondary,
		lineHeight: 1.45,
	},
	phoneCol: {
		display: "flex",
		justifyContent: "center",
		alignItems: "flex-start",
	},
}));

const generateTestNumber = () =>
	`5511${Math.floor(10000000 + Math.random() * 90000000)}`;

const TestMode = () => {
	const classes = useStyles();

	const { whatsApps, loading: whatsAppsLoading } =
		useContext(WhatsAppsContext);

	const [selectedWhatsAppId, setSelectedWhatsAppId] = useState("");
	const [clientName, setClientName] = useState(() =>
		i18n.t("testMode.client.defaultName")
	);
	const [clientNumber, setClientNumber] = useState(() =>
		generateTestNumber()
	);
	const [ticketId, setTicketId] = useState(null);
	const [messages, setMessages] = useState([]);
	const [loadingMessages, setLoadingMessages] = useState(false);
	const [sending, setSending] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);

	const testWhatsApps = whatsApps
		? whatsApps.filter((whatsApp) => whatsApp.isTest)
		: [];

	const selectedWhatsApp = testWhatsApps.find(
		(whatsApp) => whatsApp.id === selectedWhatsAppId
	);

	// Seleciona automaticamente a primeira conexão de teste disponível
	useEffect(() => {
		if (
			selectedWhatsAppId &&
			whatsApps &&
			!whatsApps.some(
				(whatsApp) => whatsApp.id === selectedWhatsAppId && whatsApp.isTest
			)
		) {
			setSelectedWhatsAppId("");
			return;
		}
		if (!selectedWhatsAppId && whatsApps) {
			const firstTest = whatsApps.find((whatsApp) => whatsApp.isTest);
			if (firstTest) {
				setSelectedWhatsAppId(firstTest.id);
			}
		}
	}, [whatsApps, selectedWhatsAppId]);

	// Trocar de conexão ou de cliente reinicia a conversa
	useEffect(() => {
		setTicketId(null);
		setMessages([]);
	}, [selectedWhatsAppId, clientNumber]);

	// Histórico do ticket após o primeiro envio
	useEffect(() => {
		if (!ticketId) return;

		let isMounted = true;
		const fetchMessages = async () => {
			setLoadingMessages(true);
			try {
				const { data } = await api.get(`/messages/${ticketId}`, {
					params: { pageNumber: 1 },
				});
				if (isMounted) {
					const loaded = data.messages || [];
					setMessages((prevMessages) => {
						const merged = [...loaded];
						prevMessages.forEach((message) => {
							if (!merged.some((m) => m.id === message.id)) {
								merged.push(message);
							}
						});
						return merged;
					});
				}
			} catch (err) {
				toastError(err);
			}
			if (isMounted) {
				setLoadingMessages(false);
			}
		};
		fetchMessages();

		return () => {
			isMounted = false;
		};
	}, [ticketId]);

	// Tempo real: entra na sala do ticket e escuta "appMessage"
	useEffect(() => {
		if (!ticketId) return;

		const socket = openSocket();

		socket.on("connect", () => socket.emit("joinChatBox", ticketId));

		socket.on("appMessage", (data) => {
			if (!data.message || data.message.ticketId !== ticketId) return;

			if (data.action === "create") {
				setMessages((prevMessages) => {
					const messageIndex = prevMessages.findIndex(
						(m) => m.id === data.message.id
					);
					if (messageIndex !== -1) {
						const updated = [...prevMessages];
						updated[messageIndex] = data.message;
						return updated;
					}
					return [...prevMessages, data.message];
				});
			}

			if (data.action === "update") {
				setMessages((prevMessages) =>
					prevMessages.map((m) =>
						m.id === data.message.id ? data.message : m
					)
				);
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [ticketId]);

	const handleSendMessage = async (body) => {
		if (!selectedWhatsAppId) {
			toast.error(i18n.t("testMode.toasts.selectConnection"));
			return false;
		}

		const number = clientNumber.replace(/\D/g, "");
		if (!number) {
			toast.error(i18n.t("testMode.toasts.invalidNumber"));
			return false;
		}

		setSending(true);
		try {
			const { data } = await api.post(
				`/test-messages/${selectedWhatsAppId}`,
				{
					fromNumber: number,
					fromName:
						clientName.trim() || i18n.t("testMode.client.defaultName"),
					body,
				}
			);
			if (data && data.ticketId && data.ticketId !== ticketId) {
				setTicketId(data.ticketId);
			}
			setSending(false);
			return true;
		} catch (err) {
			toastError(err);
			setSending(false);
			return false;
		}
	};

	const handleNewClient = () => {
		setClientNumber(generateTestNumber());
	};

	const handleConnectionCreated = (whatsApp) => {
		setDialogOpen(false);
		if (whatsApp && whatsApp.id) {
			setSelectedWhatsAppId(whatsApp.id);
		}
	};

	const sortedMessages = [...messages].sort((a, b) => {
		const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
		const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
		return dateA - dateB;
	});

	return (
		<MainContainer>
			<MainHeader>
				<Title>{i18n.t("testMode.title")}</Title>
			</MainHeader>
			<div className={classes.content}>
				<Grid container spacing={3}>
					<Grid item xs={12} md={5}>
						<Paper className={classes.configPaper} elevation={0}>
							<Typography variant="h6">
								{i18n.t("testMode.config.title")}
							</Typography>
							<Typography
								variant="body2"
								color="textSecondary"
								className={classes.configSubtitle}
							>
								{i18n.t("testMode.config.subtitle")}
							</Typography>

							{whatsAppsLoading ? (
								<div className={classes.loadingConnections}>
									<CircularProgress size={24} />
								</div>
							) : testWhatsApps.length === 0 ? (
								<div className={classes.emptyConnection}>
									<PhoneIphoneOutlinedIcon
										className={classes.emptyConnectionIcon}
									/>
									<Typography
										variant="subtitle1"
										className={classes.emptyConnectionTitle}
									>
										{i18n.t("testMode.connection.emptyTitle")}
									</Typography>
									<Typography className={classes.emptyConnectionDescription}>
										{i18n.t("testMode.connection.emptyDescription")}
									</Typography>
									<Button
										variant="contained"
										color="primary"
										startIcon={<AddOutlinedIcon />}
										onClick={() => setDialogOpen(true)}
									>
										{i18n.t("testMode.connection.createButton")}
									</Button>
								</div>
							) : (
								<>
									<FormControl
										variant="outlined"
										size="small"
										fullWidth
										className={classes.field}
									>
										<InputLabel id="test-mode-connection-label">
											{i18n.t("testMode.connection.label")}
										</InputLabel>
										<Select
											labelId="test-mode-connection-label"
											label={i18n.t("testMode.connection.label")}
											value={
												testWhatsApps.some(
													(whatsApp) => whatsApp.id === selectedWhatsAppId
												)
													? selectedWhatsAppId
													: ""
											}
											onChange={(e) =>
												setSelectedWhatsAppId(e.target.value)
											}
										>
											{testWhatsApps.map((whatsApp) => (
												<MenuItem key={whatsApp.id} value={whatsApp.id}>
													{whatsApp.name}
												</MenuItem>
											))}
										</Select>
									</FormControl>
									<Button
										size="small"
										color="primary"
										startIcon={<AddOutlinedIcon />}
										className={classes.createSmallButton}
										onClick={() => setDialogOpen(true)}
									>
										{i18n.t("testMode.connection.createButton")}
									</Button>
								</>
							)}

							<Divider className={classes.divider} />

							<Typography
								variant="subtitle2"
								className={classes.sectionTitle}
							>
								{i18n.t("testMode.client.sectionTitle")}
							</Typography>
							<TextField
								fullWidth
								variant="outlined"
								size="small"
								className={classes.field}
								label={i18n.t("testMode.client.nameLabel")}
								value={clientName}
								onChange={(e) => setClientName(e.target.value)}
							/>
							<TextField
								fullWidth
								variant="outlined"
								size="small"
								className={classes.field}
								label={i18n.t("testMode.client.numberLabel")}
								helperText={i18n.t("testMode.client.numberHelper")}
								value={clientNumber}
								onChange={(e) => setClientNumber(e.target.value)}
							/>
							<Button
								variant="outlined"
								color="primary"
								startIcon={<AutorenewOutlinedIcon />}
								onClick={handleNewClient}
							>
								{i18n.t("testMode.client.newClientButton")}
							</Button>

							<Divider className={classes.divider} />

							<div className={classes.stepsCard}>
								<Typography
									variant="subtitle2"
									className={classes.stepsTitle}
								>
									{i18n.t("testMode.steps.title")}
								</Typography>
								{[1, 2, 3].map((step) => (
									<div key={step} className={classes.stepRow}>
										<span className={classes.stepNumber}>{step}</span>
										<Typography className={classes.stepText}>
											{i18n.t(`testMode.steps.step${step}`)}
										</Typography>
									</div>
								))}
							</div>
						</Paper>
					</Grid>

					<Grid item xs={12} md={7} className={classes.phoneCol}>
						<PhoneFrame
							connectionName={
								selectedWhatsApp ? selectedWhatsApp.name : null
							}
							messages={sortedMessages}
							loading={loadingMessages}
							sending={sending}
							disabled={!selectedWhatsAppId}
							onSendMessage={handleSendMessage}
						/>
					</Grid>
				</Grid>
			</div>

			<TestConnectionDialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				onCreated={handleConnectionCreated}
			/>
		</MainContainer>
	);
};

export default TestMode;
